<script setup lang="ts">
import type { IUpgradeForm } from "@/api"
import { toTypedSchema } from "@vee-validate/zod"
import { useForm } from "vee-validate"
import { nextTick } from "vue"
import { useRouter } from "vue-router"
import { z } from "zod"
import { useCreateUpgradeRequest } from "@/api"
import { Button, Input, RadioInput, Textarea } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useConvertedRouteParam, useTranslation } from "@/composables"
import { SidebarHostEventLayout } from "@/layouts"

definePage({
  meta: {
    requiresAuth: true,
  },
})

const { t } = useTranslation()
const toast = useToast()
const router = useRouter()

const eventVaultId = useConvertedRouteParam("id", "/event/[id]/upgrade/contact-us", value => Number(value))

const validationSchema = toTypedSchema(
  z.object({
    guestsNo: z.string().min(1, t("upgrade_contact_page.validation_errors.guests_no_min")).max(40, t("upgrade_contact_page.validation_errors.guests_no_max")),
    isMultiDay: z.enum(["single", "multi"]).refine(val => val !== undefined, t("upgrade_contact_page.validation_errors.is_multi_day")),
    otherInfo: z.string().max(500, t("upgrade_contact_page.validation_errors.other_info_max")).optional(),
  }),
)

const { handleSubmit, resetForm } = useForm({
  validationSchema,
  initialValues: {
    guestsNo: "",
    isMultiDay: undefined,
    otherInfo: "",
  },
})

const { mutate: submitUpgradeRequest, isPending: isSubmitting } = useCreateUpgradeRequest()

const onSubmit = handleSubmit((values) => {
  nextTick(() => window.scrollTo({ top: 0, behavior: "smooth" }))
  const upgradeRequest: IUpgradeForm = {
    guestsNo: values.guestsNo,
    isMultiDay: values.isMultiDay === "multi",
    otherInfo: values.otherInfo,
  }

  if (eventVaultId.value) {
    submitUpgradeRequest({ upgradeRequest, eventVaultId: eventVaultId.value }, {
      onSuccess: () => {
        resetForm()
        toast.success({ description: t("upgrade_contact_page.success_message") })
        router.push(`/event/${eventVaultId.value}/upgrade`)
      },
      onError: () => {
        toast.error()
      },
    })
  }
})

const radioItems = [
  { value: "single", label: t("upgrade_contact_page.radio_items.single") },
  { value: "multi", label: t("upgrade_contact_page.radio_items.multi") },
]
</script>

<template>
  <SidebarHostEventLayout>
    <div class="flex flex-col gap-4 md:gap-8">
      <div>
        <h2 class="font-bold font-comfortaa text-lg">
          {{ t("upgrade_contact_page.title") }}
        </h2>
        <br>
        <div>
          <p>{{ t("upgrade_contact_page.description_1") }}</p>
          <p>{{ t("upgrade_contact_page.description_2") }}</p>
        </div>
      </div>

      <form @submit.prevent="onSubmit">
        <div class="flex flex-col gap-4">
          <Input :label="t('upgrade_contact_page.guests_no_label')" name="guestsNo" :disabled="isSubmitting" />
          <RadioInput label="" name="isMultiDay" orientation="vertical" :items="radioItems" :disabled="isSubmitting">
            <template #label>
              {{ t("upgrade_contact_page.is_multi_day_label") }}
            </template>
          </RadioInput>
          <Textarea :label="t('upgrade_contact_page.other_info_label')" name="otherInfo" :disabled="isSubmitting" />
          <Button type="submit" class="w-fit px-8 mt-8" :disabled="isSubmitting">
            {{ t("upgrade_contact_page.send_request_btn") }}
          </Button>
        </div>
      </form>
    </div>
  </SidebarHostEventLayout>
</template>
